/**
 * HowItWorks - "From YouTube to flashcards" closed-loop section (v11)
 *
 * Three moves, one loop: watch with bilingual subtitles → tap a line, keep
 * the proposed cards → FSRS review brings them back (timestamp replay lives
 * in step 3). Replaces MultimodalInputs + the three-move strip: the non-
 * YouTube inputs survive as one compact "other ways in" row, copy-led —
 * the hero video already carries the visual proof, no screenshots needed.
 */

import { Captions, MousePointerClick, Repeat, ClipboardList, Search } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { useLandingT } from './landing-locale'

const STEPS = [
  { icon: Captions, title: 'landing.how.step1Title', desc: 'landing.how.step1Desc' },
  { icon: MousePointerClick, title: 'landing.how.step2Title', desc: 'landing.how.step2Desc' },
  { icon: Repeat, title: 'landing.how.step3Title', desc: 'landing.how.step3Desc' },
] as const

// 2026-09-07 (landing-v12 P1-②): paste/PDF/upload moved to their own
// #bring section (OwnMaterial.tsx) — the footnote row keeps the teaser
// link down to it plus the dictionary-lookup path.
const OTHERS = [
  { icon: ClipboardList, text: 'landing.how.ownLink', href: '#bring' },
  { icon: Search, text: 'landing.how.other3' },
] as const

export function HowItWorks() {
  const revealRef = useScrollReveal()
  const t = useLandingT()

  return (
    <section id="how" className="py-16 md:py-24 bg-surface-container-low">
      <div ref={revealRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-4 text-balance">
          {t('landing.how.title')}
        </h2>
        <p className="reveal text-on-surface-variant text-lg text-center mb-12 max-w-2xl mx-auto">
          {t('landing.how.subtitle')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className={`reveal reveal-stagger-${i + 1} hover-lift rounded-2xl bg-surface p-6 sm:p-8`}
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-surface-card text-primary">
                  <step.icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <span aria-hidden className="font-headline text-4xl font-bold text-outline">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <h3 className="font-headline font-bold text-lg mb-2 text-on-surface">{t(step.title)}</h3>
              <p className="text-sm leading-relaxed text-on-surface-variant">{t(step.desc)}</p>
            </div>
          ))}
        </div>

        {/* Other ways in — compact row, no card chrome. The YouTube loop is
            the story; the non-video inputs get their own #bring section. */}
        <div className="reveal mt-12 rounded-2xl border border-outline/40 bg-surface p-6 sm:p-8">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-on-surface-variant">
            {t('landing.how.otherTitle')}
          </h3>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {OTHERS.map((other) => (
              <li key={other.text} className="flex items-start gap-2.5 text-sm leading-relaxed text-on-surface-variant">
                <other.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={1.5} />
                {'href' in other ? (
                  <a href={other.href} className="font-medium text-primary hover:underline">
                    {t(other.text)}
                  </a>
                ) : (
                  <span>{t(other.text)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
